import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { BoatSyncCard } from "@/components/boat-sync-card";
import {
  useWingCalStatuses,
  compareWingGolden,
  restoreWingGolden,
  captureWingCal,
  wingCalKeys,
} from "@/api/hooks/use-wing-cals";
import { useWingNvpStatus } from "@/api/hooks/use-wing-nvp-status";
import { useEvent } from "@/api/hooks/use-events";
import { asBoatCode, asBuildId, asEventId } from "@/types/identifiers";
import { timeAgo } from "@/lib/time";

export function WingCalsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: rows, isLoading } = useWingCalStatuses(asEventId(eventId ?? ""));
  const { data: currentEvent } = useEvent(asEventId(eventId ?? ""));
  const { getWingNvpStatus } = useWingNvpStatus();
  const qc = useQueryClient();

  return (
    <div>
      <PageHeader
        title="Wing Cals"
        subtitle="Compare the pushed wing golden against the live wing NVP, and restore if drifted."
      />

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !rows || rows.length === 0 ? (
        <EmptyState message="No builds for this event yet." className="mt-6" />
      ) : (
        <div className="mt-6 flex flex-col gap-2">
          {[...rows]
            .sort((a, b) =>
              (a.team_name ?? a.boat_code).localeCompare(b.team_name ?? b.boat_code),
            )
            .map((row) => (
              <BoatSyncCard
                key={row.build_id}
                boatCode={asBoatCode(row.boat_code)}
                teamName={row.team_name ?? row.boat_code}
                isoAlpha2={row.iso_alpha2 ?? ""}
                nvpStatus={getWingNvpStatus(row.boat_code)}
                syncType="wing-cals"
                syncId={row.build_id}
                sourceAvailable={row.has_golden}
                unavailableReason="No golden pushed yet"
                sourceLabel={
                  row.has_golden ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400">
                        Golden
                      </span>
                      <span className="text-muted-foreground">
                        from {row.source_event_name ?? "—"}
                        {row.pushed_at ? ` · ${timeAgo(row.pushed_at)}` : ""}
                      </span>
                    </span>
                  ) : undefined
                }
                compareFn={() => compareWingGolden(asBuildId(row.build_id))}
                writeBackFn={() => restoreWingGolden(asBuildId(row.build_id))}
                writeBackLabel="Restore"
                expectedHeader="Golden"
                sourceEventName={row.source_event_name}
                currentEventName={currentEvent?.name}
                acceptFn={() => captureWingCal(asBuildId(row.build_id), true)}
                acceptLabel="Golden?"
                onAcceptSuccess={() =>
                  qc.invalidateQueries({
                    queryKey: wingCalKeys.byEvent(asEventId(eventId ?? "")),
                  })
                }
              />
            ))}
        </div>
      )}
    </div>
  );
}
